import { useState, useEffect } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Building2, Plus, Pencil, Trash2, Zap, AlertCircle, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { BranchCapitalWizard } from '../components/BranchCapitalWizard';
import RegionCombobox from '../components/RegionCombobox';

export default function BranchesPage() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [regions, setRegions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '', region: '' });
  const [wizardBranch, setWizardBranch] = useState(null);

  const maxBranches = user?.subscription?.max_branches ?? 1;
  const isUnlimited = maxBranches === 0;
  const atLimit = !isUnlimited && branches.length >= maxBranches;
  const planName = user?.subscription?.plan ?? 'basic';

  const fetchBranches = async () => {
    try {
      const res = await api.get('/branches');
      setBranches(res.data);
    } catch { toast.error('Failed to load branches'); }
  };

  const fetchRegions = async () => {
    try {
      const res = await api.get('/branches/regions');
      setRegions(res.data?.regions || []);
    } catch { /* non-blocking */ }
  };

  useEffect(() => { fetchBranches(); fetchRegions(); }, []);

  const openCreate = () => {
    if (atLimit) {
      toast.error(`Branch limit reached (${branches.length}/${maxBranches}). Upgrade your plan to add more branches.`);
      return;
    }
    setEditing(null); setForm({ name: '', address: '', phone: '', region: '' }); setDialogOpen(true);
  };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, address: b.address || '', phone: b.phone || '', region: b.region || '' }); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/branches/${editing.id}`, form);
        toast.success('Branch updated');
      } else {
        await api.post('/branches', form);
        toast.success('Branch created');
      }
      setDialogOpen(false);
      fetchBranches();
      fetchRegions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving branch'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this branch?')) return;
    try {
      await api.delete(`/branches/${id}`);
      toast.success('Branch deleted');
      fetchBranches();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="branches-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--text-primary)' }}>Branches</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage your business locations
            {!isUnlimited && (
              <span className={`ml-2 font-medium ${atLimit ? 'text-red-500' : 'text-slate-600'}`}>
                ({branches.length}/{maxBranches} used)
              </span>
            )}
            {isUnlimited && <span className="ml-2 text-emerald-600 font-medium">(Unlimited)</span>}
          </p>
        </div>
        <Button
          data-testid="create-branch-btn"
          onClick={openCreate}
          disabled={atLimit}
          title={atLimit ? `Branch limit reached (${branches.length}/${maxBranches}). Upgrade to add more.` : 'Add Branch'}
          className={atLimit ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-[#1A4D2E] hover:bg-[#14532d] text-white'}
        >
          <Plus size={16} className="mr-2" /> Add Branch
        </Button>
      </div>

      {atLimit && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="branch-limit-warning">
          <AlertCircle size={18} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Branch limit reached</p>
            <p className="text-xs text-amber-700 mt-1">
              Your <span className="font-semibold capitalize">{planName}</span> plan allows {maxBranches} branch{maxBranches !== 1 ? 'es' : ''}.
              {' '}To add more branches, upgrade your plan or purchase an Extra Branch add-on.
            </p>
          </div>
        </div>
      )}

      <Card style={{ border: "1px solid var(--border-default)" }}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow style={{ backgroundColor: "#F1F0ED" }}>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Region</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Address</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium">Phone</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-medium w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {branches.map(b => (
                <TableRow key={b.id} className="table-row-hover">
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Building2 size={16} className="text-slate-400" strokeWidth={1.5} />
                      {b.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {b.region ? (
                      <span className="inline-flex items-center gap-1 text-xs"><MapPin size={12} className="text-slate-400" />{b.region}</span>
                    ) : '—'}
                  </TableCell>
                  <TableCell className="text-slate-500">{b.address || '—'}</TableCell>
                  <TableCell className="text-slate-500">{b.phone || '—'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" title="Quick-Fill Capital"
                        data-testid={`quickfill-${b.id}`}
                        onClick={() => setWizardBranch(b)}
                        className="text-amber-600 hover:text-amber-700">
                        <Zap size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`edit-branch-${b.id}`} onClick={() => openEdit(b)}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`delete-branch-${b.id}`} onClick={() => handleDelete(b.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!branches.length && (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No branches yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>{editing ? 'Edit Branch' : 'New Branch'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Branch Name</Label>
              <Input data-testid="branch-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Main Branch" />
            </div>
            <div>
              <Label>Address</Label>
              <Input data-testid="branch-address-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Branch address" />
            </div>
            <div>
              <Label>Region <span className="text-slate-400 font-normal">(for grouped price propagation)</span></Label>
              <RegionCombobox value={form.region} regions={regions} onChange={(r) => setForm({ ...form, region: r })} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="branch-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Contact number" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button data-testid="save-branch-btn" onClick={handleSave} className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Branch Capital Quick-Fill Wizard */}
      <BranchCapitalWizard
        open={!!wizardBranch}
        onClose={() => setWizardBranch(null)}
        targetBranch={wizardBranch}
      />
    </div>
  );
}
